import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Calendar, Tag, ArrowRight, Loader2, User, Clock, ArrowLeft, Share2 } from "lucide-react";
import { api } from "@/lib/config";
import { Reveal } from "@/lib/motion";
import Seo from "@/lib/Seo";
import { toast } from "sonner";

// Configure marked
marked.setOptions({ breaks: true, gfm: true });

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const months = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const readingMinutes = (md) => Math.max(1, Math.round((md || "").split(/\s+/).length / 200));

// ─── /blog list ──────────────────────────────────
export function BlogList() {
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeTag = params.get("tag");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/blog/posts", { params: activeTag ? { tag: activeTag } : {} }),
      api.get("/blog/tags"),
    ])
      .then(([p, t]) => { setPosts(p.data); setTags(t.data); })
      .catch(() => toast.error("Errore caricamento articoli"))
      .finally(() => setLoading(false));
  }, [activeTag]);

  const clearTag = () => setParams({});

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <Seo title="Blog & News · Gefion Evolution" description="Novità, guide tecniche e approfondimenti dal team Gefion Evolution: impianti industriali, automazione, energia rinnovabile, taglio laser." canonical="/blog" />

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pb-16 border-b border-[#141414]">
        <div className="num-chip mb-4"><span className="dot" /> Blog & News</div>
        <Reveal>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">
            Idee, guide,<br/><span className="italic-serif">novità.</span>
          </h1>
        </Reveal>
        <p className="text-white/60 mt-6 max-w-2xl md:text-lg">Approfondimenti tecnici, best practice e novità dal mondo industriale — direttamente dai tecnici Gefion Evolution.</p>
        {tags.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2 items-center">
            <span className="font-mono-tech text-[10px] text-white/40 mr-2">◐ FILTRA PER TAG</span>
            <button
              onClick={clearTag}
              className={`px-3 py-1.5 border font-mono-tech text-[10px] uppercase transition-colors ${!activeTag ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#1a1a1a] text-white/50 hover:text-white"}`}
              data-testid="blog-tag-all"
            >
              Tutti
            </button>
            {tags.map((t) => (
              <button
                key={t.tag}
                onClick={() => setParams({ tag: t.tag })}
                data-testid={`blog-tag-${t.tag}`}
                className={`px-3 py-1.5 border font-mono-tech text-[10px] uppercase transition-colors ${activeTag === t.tag ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#1a1a1a] text-white/50 hover:text-white"}`}
              >
                {t.tag} <span className="opacity-40">({t.count})</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-3 font-mono-tech text-white/40 text-sm">
            <Loader2 size={16} className="animate-spin" /> Caricamento articoli...
          </div>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-[#222] p-16 text-center">
            <div className="font-display text-3xl text-white/80 mb-3">
              {activeTag ? `Nessun articolo per "${activeTag}"` : "Presto qui i primi articoli"}
            </div>
            <div className="font-mono-tech text-[11px] text-white/40">
              {activeTag ? "Prova con un tag diverso" : "Il team sta preparando i primi contenuti."}
            </div>
            {activeTag && (
              <button onClick={clearTag} className="btn-primary mt-8 inline-flex">Vedi tutti</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-posts-grid">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group block border border-[#1a1a1a] bg-[#0a0a0a] hover:border-[#ff4b00]/40 transition-colors overflow-hidden"
    >
      {post.cover_image ? (
        <div className="aspect-[16/10] overflow-hidden bg-[#050505] border-b border-[#141414]">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="aspect-[16/10] blueprint-grid border-b border-[#141414] flex items-center justify-center">
          <div className="font-display text-6xl text-[#ff4b00]/20">◐</div>
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3 font-mono-tech text-[10px] text-white/40 mb-3">
          {post.category && <><span className="text-[#ff4b00]">◐ {post.category}</span><span>·</span></>}
          <span className="flex items-center gap-1"><Calendar size={10} /> {fmtDate(post.published_at || post.created_at)}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock size={10} /> {readingMinutes(post.content_md)} min</span>
        </div>
        <div className="font-display text-2xl leading-tight text-white group-hover:text-[#ff4b00] transition-colors">{post.title}</div>
        {post.subtitle && <div className="text-white/60 text-sm mt-3 line-clamp-3">{post.subtitle}</div>}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {post.tags.slice(0, 3).map((t) => (
              <span key={t} className="font-mono-tech text-[9px] text-white/40 uppercase">#{t}</span>
            ))}
          </div>
        )}
        <div className="mt-5 pt-5 border-t border-[#141414] font-mono-tech text-[10px] text-[#ff4b00] flex items-center gap-2">
          Leggi articolo <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

// ─── /blog/:slug ─────────────────────────────────
export function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setPost(null);
    api.get(`/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        // Load related by first tag
        if (r.data.tags && r.data.tags[0]) {
          api.get("/blog/posts", { params: { tag: r.data.tags[0], limit: 4 } })
            .then((rr) => setRelated((rr.data || []).filter((x) => x.slug !== r.data.slug).slice(0, 3)))
            .catch(() => {});
        }
      })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, text: post.subtitle, url }); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    }
  };

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white/40 min-h-screen"><Loader2 size={20} className="animate-spin mx-auto" /></div>;
  if (notFound) return (
    <div className="pt-40 min-h-screen">
      <div className="max-w-md mx-auto px-6 text-center">
        <div className="font-display text-6xl text-[#ff4b00] mb-4">404</div>
        <div className="font-display text-2xl text-white mb-6">Articolo non trovato</div>
        <button onClick={() => nav("/blog")} className="btn-primary inline-flex">← Torna al blog</button>
      </div>
    </div>
  );
  if (!post) return null;

  const html = DOMPurify.sanitize(marked.parse(post.content_md || ""), {
    ALLOWED_TAGS: ["h1","h2","h3","h4","h5","h6","p","a","b","strong","i","em","u","ul","ol","li","blockquote","code","pre","img","hr","br","span","div"],
    ALLOWED_ATTR: ["href","src","alt","title","target","rel","class"],
  });

  return (
    <div className="pt-24 pb-24 min-h-screen">
      <Seo
        title={`${post.seo_title || post.title} · Gefion Evolution`}
        description={post.seo_description || post.subtitle || post.title}
        canonical={`/blog/${post.slug}`}
        ogImage={post.cover_image}
      />

      <article>
        {/* Hero */}
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-8 pb-12">
          <button onClick={() => nav("/blog")} className="font-mono-tech text-[10px] text-white/40 hover:text-[#ff4b00] flex items-center gap-2 mb-10">
            <ArrowLeft size={12} /> Torna al blog
          </button>
          {post.category && (
            <div className="num-chip mb-4"><span className="dot" /> {post.category}</div>
          )}
          <Reveal>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.98] text-white mb-6" data-testid="blog-post-title">{post.title}</h1>
          </Reveal>
          {post.subtitle && (
            <p className="italic-serif text-xl md:text-2xl text-white/70 leading-snug max-w-2xl">{post.subtitle}</p>
          )}
          <div className="flex items-center gap-4 mt-8 font-mono-tech text-[11px] text-white/50">
            <span className="flex items-center gap-2"><User size={12} /> {post.author || "Gefion Evolution"}</span>
            <span>·</span>
            <span className="flex items-center gap-2"><Calendar size={12} /> {fmtDate(post.published_at || post.created_at)}</span>
            <span>·</span>
            <span className="flex items-center gap-2"><Clock size={12} /> {readingMinutes(post.content_md)} min di lettura</span>
            <button onClick={share} className="ml-auto flex items-center gap-2 hover:text-[#ff4b00]" data-testid="blog-share-btn">
              <Share2 size={12} /> Condividi
            </button>
          </div>
        </div>

        {/* Cover */}
        {post.cover_image && (
          <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-16">
            <div className="aspect-[16/9] overflow-hidden border border-[#141414]">
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-[720px] mx-auto px-6 md:px-10">
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: html }}
            data-testid="blog-post-content"
          />
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="max-w-[720px] mx-auto px-6 md:px-10 mt-16 pt-8 border-t border-[#141414]">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">◐ TAG</div>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="px-3 py-1.5 border border-[#1a1a1a] font-mono-tech text-[10px] uppercase text-white/60 hover:text-[#ff4b00] hover:border-[#ff4b00] transition-colors">
                  <Tag size={9} className="inline mr-1" />{t}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related */}
        {related.length > 0 && (
          <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-24 pt-16 border-t border-[#141414]">
            <div className="num-chip mb-6"><span className="dot" /> Continua a leggere</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => <PostCard key={r.id} post={r} />)}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
